// frontend-crm/src/components/Layout.js
import React, { useState, useContext } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Typography, Avatar, Divider, AppBar, Toolbar, IconButton, Tooltip, Chip, useTheme
} from '@mui/material';
import { ColorModeContext } from '../App';
import {
  Dashboard as DashboardIcon,
  LocalShipping as PedidosIcon,
  People as MensajerosIcon,
  Person as ClientesIcon,
  AttachMoney as FinanzasIcon,
  Map as MapIcon,
  WhatsApp as WhatsAppIcon,
  AccountBalanceWallet as BalanceIcon,
  Assessment as ConsolidatedIcon,
  ExitToApp as LogoutIcon,
  Menu as MenuIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon
} from '@mui/icons-material';

const DRAWER_WIDTH = 240;

const NAV_ITEMS = [
  { label: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { label: 'Pedidos', icon: <PedidosIcon />, path: '/pedidos' },
  { label: 'Mensajeros', icon: <MensajerosIcon />, path: '/mensajeros' },
  { label: 'Clientes', icon: <ClientesIcon />, path: '/clientes' },
  { label: 'Finanzas', icon: <FinanzasIcon />, path: '/finanzas' },
  { label: 'Tracking', icon: <MapIcon />, path: '/tracking' },
  { label: 'WhatsApp', icon: <WhatsAppIcon />, path: '/chat' },
  { label: 'Balance', icon: <BalanceIcon />, path: '/balance' },
  { label: 'Consolidado', icon: <ConsolidatedIcon />, path: '/consolidated' },
];

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const [mobileOpen, setMobileOpen] = useState(false);

  const user = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const DrawerContent = () => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: theme.palette.background.paper, borderRight: `1px solid ${theme.palette.divider}` }}>
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 36, height: 36, fontSize: 16 }}>🚀</Avatar>
        <Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: theme.palette.text.primary }}>CRM Mensajería</Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>{user.nombre || 'Admin'}</Typography>
        </Box>
      </Box>

      <List sx={{ flex: 1, px: 1, pt: 1.5 }}>
        {NAV_ITEMS.map(item => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem disablePadding key={item.path} sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => { navigate(item.path); setMobileOpen(false); }}
                sx={{
                  borderRadius: 2, py: 1, px: 2,
                  bgcolor: isActive ? `${theme.palette.primary.main}1A` : 'transparent',
                  border: isActive ? `1px solid ${theme.palette.primary.main}4D` : '1px solid transparent',
                  '&:hover': { bgcolor: `${theme.palette.primary.main}1A`, border: `1px solid ${theme.palette.primary.main}33` },
                  transition: 'all 0.2s',
                }}
              >
                <ListItemIcon sx={{ color: isActive ? theme.palette.primary.main : theme.palette.text.secondary, minWidth: 36 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: isActive ? 600 : 400, color: isActive ? theme.palette.text.primary : theme.palette.text.secondary }} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider sx={{ mx: 1 }} />
      <List sx={{ px: 1, pb: 2 }}>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout} sx={{ borderRadius: 2, '&:hover': { bgcolor: 'rgba(255,72,72,0.1)' } }}>
            <ListItemIcon sx={{ color: theme.palette.error.main, minWidth: 36 }}><LogoutIcon /></ListItemIcon>
            <ListItemText primary="Salir" primaryTypographyProps={{ fontSize: '0.875rem', color: theme.palette.error.main }} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box', border: 'none' }
        }}
      >
        <DrawerContent />
      </Drawer>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}
      >
        <DrawerContent />
      </Drawer>

      {/* Main */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Mobile AppBar */}
        <AppBar position="static" sx={{ bgcolor: theme.palette.background.paper, borderBottom: `1px solid ${theme.palette.divider}`, display: { sm: 'none' }, boxShadow: 'none' }}>
          <Toolbar>
            <IconButton onClick={() => setMobileOpen(true)} sx={{ color: theme.palette.text.primary, mr: 2 }}><MenuIcon /></IconButton>
            <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: 700, flexGrow: 1 }}>CRM Mensajería</Typography>
            <IconButton sx={{ ml: 1 }} onClick={colorMode.toggleColorMode} color="inherit">
              {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon sx={{ color: theme.palette.text.primary }} />}
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* Desktop TopBar or just a Theme toggle */}
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'flex-end', p: 1 }}>
           <IconButton sx={{ ml: 1 }} onClick={colorMode.toggleColorMode} color="inherit">
              {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon sx={{ color: theme.palette.text.primary }} />}
            </IconButton>
        </Box>

        <Box component="main" sx={{ flex: 1, overflow: 'auto', p: 0 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
